// /parts/boms/:key — one product's parts list and what it can build. The key
// is a drone's model code (F021) or any other product's catalog id.
//
// The page holds ONE BomDetail (`detail`): the active revision, or an older one
// the person asked to look at. Every write answers with the full refreshed
// BomDetail, so a write replaces state and nothing is refetched, except when a
// revision starts (the revision list changes, so the product is read again).
//
// Older revisions are read-only on the server (`bom_inactive`), and the page
// never offers an edit on one, so that error is a safety net, not a path.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import Popover from '../components/ui/Popover';
import Reveal from '../components/ui/Reveal';
import { useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { DataTable } from '../components/table/DataTable';
import { laneLabel } from '../components/review/fieldStyles';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Tag } from '../components/ui/Tag';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { formatTimestamp } from '../utils/date-utils';
import {
  bomErrorMessage,
  bomErrorStatus,
  productCode,
  productName,
  useBomsApi,
  variantLabel,
  type BomDetail,
  type BomLine,
  type Capacity,
  type HeaderPatch,
  type LinePatch,
  type NewLineInput,
  type ProductDetail,
} from './bomsApi';
import { LINE_COLUMNS, pendKey, type EditableField, type LineMeta } from './BomLines';
import BomAddLine from './BomAddLine';
import BomFigures from './BomFigures';

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const getLineId = (l: BomLine) => String(l.id);

function without<T>(obj: Record<string | number, T>, key: string | number) {
  const copy = { ...obj };
  delete copy[key];
  return copy;
}

const showLine = (lineId: number) => {
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  document
    .getElementById(`bom-line-${lineId}`)
    ?.scrollIntoView?.({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
};

// ── States ───────────────────────────────────────────────────────────────────

const PageLoading: React.FC = () => (
  <PageLayout width="wide" kind="record" className="pt-4">
    <LogoSpinner label="Loading the parts list" />
  </PageLayout>
);

const Notice: React.FC<{ children: React.ReactNode; onRetry?: () => void }> = ({ children, onRetry }) => (
  <PageLayout width="wide" kind="record" className="pt-4">
    <Alert tone={onRetry ? 'error' : 'warning'} live="alert" onRetry={onRetry}>
      {children}
    </Alert>
  </PageLayout>
);

// ── Pieces ───────────────────────────────────────────────────────────────────

const LabelEditor: React.FC<{
  label: string | null;
  canEdit: boolean;
  onSave: (label: string | null) => Promise<void>;
}> = ({ label, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const finished = useRef(false);
  const text = label || 'Untitled list';

  if (!canEdit) return <span className="text-sm font-medium text-on-surface">{text}</span>;

  if (editing) {
    const finish = async (save: boolean) => {
      if (finished.current) return;
      finished.current = true;
      setEditing(false);
      const next = draft.trim() === '' ? null : draft.trim();
      if (!save || next === (label ?? null)) return;
      setError(null);
      try {
        await onSave(next);
      } catch (err) {
        setError(bomErrorMessage(err));
      }
    };
    return (
      <Field
        autoFocus
        value={draft}
        maxLength={80}
        aria-label="Parts list name"
        placeholder="Name this list, such as Rev C"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            finish(false);
          }
        }}
        className="w-64"
      />
    );
  }

  return (
    <span className="inline-flex flex-col">
      <button
        type="button"
        onClick={() => {
          finished.current = false;
          setDraft(label ?? '');
          setEditing(true);
        }}
        className={`group inline-flex items-center gap-2 rounded px-1 py-0.5 -mx-1 text-sm font-medium text-on-surface hover:bg-surface-container-high ${FOCUS}`}
      >
        {text}
        <span className="text-xs font-normal text-on-surface-variant group-hover:text-on-surface">Rename</span>
      </button>
      <Alert show={!!error} variant="text" className="!text-xs">
        Not saved: {error}
      </Alert>
    </span>
  );
};

const StartForm: React.FC<{
  buttonText: string;
  onStart: (label?: string) => Promise<void>;
  autoFocus?: boolean;
}> = ({ buttonText, onStart, autoFocus }) => {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onStart(label.trim() || undefined);
    } catch (err) {
      setError(bomErrorMessage(err, 'The list was not started. Try again.'));
      setBusy(false);
    }
  };

  return (
    <div>
      <label htmlFor="bom-start-label" className={laneLabel}>
        Name, optional
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Field
          id="bom-start-label"
          autoFocus={autoFocus}
          value={label}
          maxLength={80}
          placeholder="Rev C"
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              go();
            }
          }}
          className="min-w-0 flex-1"
        />
        <Button variant="filled" onClick={go} disabled={busy}>
          {busy ? 'Starting…' : buttonText}
        </Button>
      </div>
      <Alert show={!!error} variant="text" className="mt-2">
        {error}
      </Alert>
    </div>
  );
};

/** The header action. A popover under its own button: the confirm lands where the pointer is. */
const RevisionStarter: React.FC<{ onStart: (label?: string) => Promise<void> }> = ({ onStart }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLElement | null>(null);

  return (
    <div ref={wrap} className="relative">
      <SecondaryAction
        onClick={() => {
          trigger.current = wrap.current?.querySelector('button') ?? null;
          setOpen((o) => !o);
        }}
      >
        Start a new revision
      </SecondaryAction>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={wrap}
        returnTo={trigger}
        label="Start a new revision"
        align="end"
        width="md"
        hideClose
      >
        <p className="mb-3 text-sm text-on-surface-variant">
          Copies this list and keeps the current one as an older revision.
        </p>
        <StartForm
          autoFocus
          buttonText="Start revision"
          onStart={async (label) => {
            await onStart(label);
            setOpen(false);
          }}
        />
      </Popover>
    </div>
  );
};

const CapacityReadout: React.FC<{
  capacity: Capacity[];
  hasLines: boolean;
  marginPercent: number;
}> = ({ capacity, hasLines, marginPercent }) => {
  if (capacity.length === 0) {
    return (
      <Panel label="Builds today">
        <EmptyState variant="line">
          {hasLines
            ? 'No build figure for this list.'
            : 'Add parts to the list and this shows how many units the shelf can build.'}
        </EmptyState>
      </Panel>
    );
  }
  return (
    <section aria-label="Builds today" aria-live="polite" className="flex flex-wrap gap-3">
      {capacity.map((c) => (
        <div
          key={c.variant ?? 'all'}
          className="min-w-[15rem] flex-1 rounded-md bg-surface-container-low px-5 py-4"
        >
          <div className="text-xs font-medium text-on-surface-variant">
            {c.variant === null ? 'Builds today' : `${variantLabel(c.variant)} builds today`}
          </div>
          {/* Re-keyed on the figure: a number that changes lands again. The one
              charm moment on this page, and it only fires when the answer moved. */}
          <div
            key={c.builds}
            className="mt-1 text-6xl font-bold leading-none tabular-nums text-primary motion-safe:animate-rowLand"
          >
            {c.builds}
          </div>
          <Reveal show={marginPercent > 0}>
            <div className="mt-2 text-sm tabular-nums text-on-surface-variant">
              {c.raw_builds} on the shelf, less {marginPercent}% for failures
            </div>
          </Reveal>
          <Reveal show={!!c.limited_by}>
            <div className="mt-3 text-sm text-on-surface-variant">
              limited by{' '}
              <button
                type="button"
                onClick={() => c.limited_by && showLine(c.limited_by.line_id)}
                className={`rounded font-medium text-on-surface hover:underline ${FOCUS}`}
              >
                {c.limited_by?.name}
              </button>
            </div>
          </Reveal>
        </div>
      ))}
    </section>
  );
};

// ── The page ─────────────────────────────────────────────────────────────────

type Status = 'loading' | 'ready' | 'error' | 'notfound';

const BomPage: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const boms = useBomsApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  // Admin bypasses the write gate; the hourly rate is the one thing it does not.
  const canWrite = !!(user.isadmin || user.can_admin || user.can_manufacturing);
  const canFinancial = !!user.can_financial;

  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState('');
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [detail, setDetail] = useState<BomDetail | null>(null);
  const [switching, setSwitching] = useState(false);
  const [pending, setPending] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [hiddenMemberIds, setHiddenMemberIds] = useState<Set<number>>(new Set());

  // Writes can overlap (two cells edited in a row). Each answer is the whole
  // list, so only the latest-started write may replace state.
  const seq = useRef(0);
  const applied = useRef(0);

  // The key the latest load asked for: a slower answer for a product the URL
  // has moved off is dropped.
  const loadingKey = useRef(key);

  const load = useCallback(async () => {
    setStatus('loading');
    loadingKey.current = key;
    try {
      const p = await boms.getProduct(key);
      if (loadingKey.current !== key) return;
      setProduct(p);
      setDetail(p.active);
      setPending({});
      setErrors({});
      setConfirmingId(null);
      setStatus('ready');
    } catch (err) {
      if (loadingKey.current !== key) return;
      if (bomErrorStatus(err) === 404) {
        setStatus('notfound');
      } else {
        setLoadError(bomErrorMessage(err, 'The parts list did not load.'));
        setStatus('error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  const applyDetail = (d: BomDetail) => {
    // Someone viewing an older revision keeps it: a late answer for another list
    // only refreshes the product's copy below.
    setDetail((cur) => (cur && !cur.bom.is_active && cur.bom.id !== d.bom.id ? cur : d));
    if (d.bom.is_active) {
      setProduct((p) =>
        p
          ? {
              ...p,
              active: d,
              revisions: p.revisions.map((r) =>
                r.id === d.bom.id ? { ...r, ...d.bom, line_count: d.lines.length } : r
              ),
            }
          : p
      );
    }
  };

  const run = async (call: () => Promise<BomDetail>) => {
    const mine = ++seq.current;
    const d = await call();
    if (mine > applied.current) {
      applied.current = mine;
      applyDetail(d);
    }
  };

  const bomId = detail?.bom.id;
  const isActive = !!detail?.bom.is_active;
  const canEdit = canWrite && isActive;

  const onPatch: LineMeta['onPatch'] = async (line, field: EditableField, value) => {
    const key = pendKey(line.id, field);
    setPending((p) => ({ ...p, [key]: value }));
    setErrors((e) => without(e, line.id));
    try {
      await run(() => boms.patchLine(line.id, { [field]: value } as LinePatch));
    } catch (err) {
      setErrors((e) => ({ ...e, [line.id]: bomErrorMessage(err) }));
    } finally {
      setPending((p) => without(p, key));
    }
  };

  const onRemove = async (line: BomLine) => {
    setConfirmingId(null);
    try {
      await run(() => boms.deleteLine(line.id));
    } catch (err) {
      toast.error(bomErrorMessage(err, 'The line was not removed. Try again.'));
    }
  };

  const onAdd = async (input: NewLineInput) => {
    if (!bomId) return;
    await run(() => boms.addLine(bomId, input));
  };

  const onSaveHeader = async (patch: HeaderPatch) => {
    if (!bomId) return;
    await run(() => boms.patchHeader(bomId, patch));
  };

  const onStart = async (label?: string) => {
    const created = await boms.startRevision(key, label);
    seq.current += 1;
    applied.current = seq.current;
    applyDetail(created);
    // The revision list changed, so the product is read once more.
    try {
      const p = await boms.getProduct(key);
      setProduct(p);
      setDetail(p.active ?? created);
    } catch {
      // The new list is already on screen; the switcher catches up on reload.
    }
    toast.success(created.bom.label ? `Started ${created.bom.label}.` : 'Started a new parts list.');
  };

  const onPickRevision = async (id: string) => {
    if (!product || id === bomId) return;
    if (product.active && id === product.active.bom.id) {
      setDetail(product.active);
      return;
    }
    setSwitching(true);
    try {
      setDetail(await boms.getBom(id));
    } catch (err) {
      toast.error(bomErrorMessage(err, 'That revision did not load.'));
    } finally {
      setSwitching(false);
    }
  };

  const limiterIds = useMemo(
    () =>
      new Set<number>(
        (detail?.capacity ?? []).flatMap((c) => (c.limited_by ? [c.limited_by.line_id] : []))
      ),
    [detail]
  );

  const columnVisibility = useMemo(() => ({ actions: canEdit }), [canEdit]);

  if (status === 'loading') return <PageLoading />;

  if (status === 'notfound') {
    return (
      <Notice>
        <p className="text-on-surface">There is no product called {key}.</p>
        <p className="mt-1 text-on-surface-variant">
          A drone model appears when its first drone is minted. Check the code, or pick one from
          the list.
        </p>
        <BackLink to="/parts/boms" label="Product BOMs" className="mt-3" />
      </Notice>
    );
  }

  if (status === 'error' || !product) {
    return (
      <Notice onRetry={load}>{loadError || 'The parts list did not load.'}</Notice>
    );
  }

  const info = product.product;
  const title = productCode(info);
  const name = productName(info);
  const lineMeta: LineMeta = {
    canEdit,
    limiterIds,
    pending,
    errors,
    confirmingId,
    hiddenMemberIds,
    onToggleMembers: (lineId) =>
      setHiddenMemberIds((prev) => {
        const next = new Set(prev);
        if (!next.delete(lineId)) next.add(lineId);
        return next;
      }),
    onPatch,
    onFail: (lineId, message) => setErrors((e) => ({ ...e, [lineId]: message })),
    onAskRemove: setConfirmingId,
    onRemove,
  };

  return (
    <PageLayout width="wide"
      kind="record"
      className="space-y-5 pt-4"
      header={
      <PageHeader
        title={title}
        helpTitle="About a parts list"
        lede={name !== title ? name : undefined}
        meta={
          !info.is_active ? (
            <Tag>Retired product</Tag>
          ) : undefined
        }
        help={
          <>
            <p>
              The parts list says what goes into one unit and how many of each. A line can name
              a whole part family, so any revision of it counts, or one specific part.
            </p>
            <p className="text-on-surface font-medium">Builds today</p>
            <p>
              How many units the shelf could build right now, less the failure margin set on the
              Product BOMs page. The number before the margin sits under it. The line that runs
              out first sets the number and is named under it. A line tagged Pyro or Standard
              only counts toward that build.
            </p>
            <p className="text-on-surface font-medium">Scanned or not scanned</p>
            <p>
              A scanned line is identified at the fit, by board UID or sticker, and written to the
              install events. A line that is not scanned is deducted at the next checkpoint.
              Scanning needs the part tracked per unit: the per-instance switch on its catalog
              page.
            </p>
            <p className="text-on-surface font-medium">Revisions</p>
            <p>
              Changing a list that has been used is done by starting a new revision. The old one
              stays on file, read only.
            </p>
          </>
        }
        actions={
          <>
            <SecondaryAction variant="text" to="/parts/boms">
              All products
            </SecondaryAction>
            {canWrite && product.active && <RevisionStarter onStart={onStart} />}
          </>
        }
      />
      }
    >
      {!detail ? (
        <EmptyState
          title="No parts list yet"
          action={canWrite ? <div className="w-full max-w-md text-left"><StartForm buttonText="Start a parts list" onStart={onStart} /></div> : undefined}
        >
          {canWrite
            ? `Start the list for ${name}, then add each part and how many go into one unit. The shelf count turns into a build figure as you go.`
            : `Nobody has started a list for ${name}. Someone in manufacturing can.`}
        </EmptyState>
      ) : (
        <div className={`space-y-5 ${switching ? 'opacity-60' : ''}`} aria-busy={switching}>
          <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
            <div>
              <div className={laneLabel}>Parts list</div>
              <LabelEditor
                key={detail.bom.id}
                label={detail.bom.label}
                canEdit={canEdit}
                onSave={(label) => onSaveHeader({ label })}
              />
            </div>
            <Reveal show={product.revisions.length > 1}>
              <div>
                <label htmlFor="bom-revision" className={laneLabel}>
                  Revision
                </label>
                <Select
                  id="bom-revision"
                  label="Revision"
                  width="fit"
                  value={String(detail.bom.id)}
                  onChange={onPickRevision}
                  options={product.revisions.map((r) => ({
                    key: String(r.id),
                    label:
                      (r.label || 'Untitled list') +
                      ', ' +
                      formatTimestamp(r.created_at) +
                      (r.is_active ? ' (current)' : ' (older)'),
                  }))}
                />
              </div>
            </Reveal>
          </div>

          <Alert
            show={!isActive}
            tone="info"
            title="Older revision, read only."
            action={
              product.active ? (
                <Button variant="tonal" onClick={() => onPickRevision(product.active!.bom.id)}>
                  Back to the current list
                </Button>
              ) : undefined
            }
          >
            Start a new revision from the current list to change anything.
          </Alert>

          <CapacityReadout
            capacity={detail.capacity}
            hasLines={detail.lines.length > 0}
            marginPercent={detail.failure_margin_percent}
          />

          <DataTable<BomLine>
            data={detail.lines}
            columns={LINE_COLUMNS}
            getRowId={getLineId}
            columnVisibility={columnVisibility}
            meta={lineMeta}
            rowProps={(l) => ({ id: `bom-line-${l.id}` })}
            strip={
              <span className="text-xs text-on-surface-variant tabular-nums">
                {detail.lines.length} {detail.lines.length === 1 ? 'line' : 'lines'}
              </span>
            }
            empty={
              <EmptyState
                variant="area"
                title="No parts on this list yet."
              >
                {canEdit ? 'Pick a part family or a specific part below and press Enter.' : undefined}
              </EmptyState>
            }
          />

          {canEdit && <BomAddLine onAdd={onAdd} />}

          <BomFigures
            bom={detail.bom}
            canEdit={canEdit}
            canFinancial={canFinancial}
            onSave={onSaveHeader}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default BomPage;
